import { css } from '@emotion/react';
import {
  Button,
  CheckBox,
  color,
  FormGroup,
  Input,
  spacing,
  typography
} from '@uniquegood/realworld-studio-design';
import axios, { AxiosError } from 'axios';
import { FormEvent, useEffect, useState } from 'react';

import { CORE_API_BASE_URL } from '@/api';
import { getAccessToken } from '@/auth';
import ConnectCard from '../components/ConnectCard';
import Countdown from '../components/Countdown';
import UserInfoCard from '../components/UserInfoCard';
import {
  SchoolConnectSignupError,
  SchoolConnectSignupRequest,
  SchoolConnectSignupResponse
} from '../types';

interface UnauthenticatedStateProps {
  authCode: string;
  email: string;
  nickname: string;
  remainingSeconds: number | null;
  onLogin: () => void;
  onSignupSuccess: () => void;
}

const SIGNUP_ENDPOINT = '/api/connect/school/signup';
const NICKNAME_PATTERN = /^[A-Za-z0-9_-]{2,20}$/;
const PASSWORD_PATTERN =
  /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+\-=[\]{}|;':",./<>?~]{8,20}$/;
const coreApiWithoutAuth = axios.create({ baseURL: CORE_API_BASE_URL });

/**
 * success-unauthenticated state.
 *
 * exchange는 성공했지만 사용자가 미로그인 상태.
 * 기존 사용자는 로그인하면 `/connect/resume`으로 돌아오고, 신규 사용자는 여기서 가입 후 같은 흐름을 이어간다.
 */
export default function UnauthenticatedState({
  authCode,
  email,
  nickname,
  remainingSeconds,
  onLogin,
  onSignupSuccess
}: UnauthenticatedStateProps) {
  const [displayName, setDisplayName] = useState(nickname);
  const [password, setPassword] = useState('');
  const [useConsent, setUseConsent] = useState(false);
  const [userDataConsent, setUserDataConsent] = useState(false);
  const [ageConsent, setAgeConsent] = useState(false);
  const [signupErrorMessage, setSignupErrorMessage] = useState<string | null>(null);
  const [isSigningUp, setIsSigningUp] = useState(false);

  useEffect(() => {
    setDisplayName(nickname);
  }, [nickname]);

  const handleSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationMessage = validateSignupForm({
      nickname: displayName,
      password,
      useConsent,
      userDataConsent,
      ageConsent
    });
    if (validationMessage) {
      setSignupErrorMessage(validationMessage);
      return;
    }

    setSignupErrorMessage(null);
    setIsSigningUp(true);
    try {
      const token = await signupWithSchoolAuthCode({
        authCode,
        nickname: displayName.trim(),
        password,
        useConsent,
        userDataConsent,
        ageConsent,
        receiveMarketingNotificationConsent: false
      });

      localStorage.setItem('accessToken', token.token);
      localStorage.setItem('refreshToken', token.refreshToken);
      localStorage.setItem('expiresAt', token.expiresAt);
      getAccessToken.clear();
      onSignupSuccess();
    } catch (error) {
      setSignupErrorMessage(
        resolveSignupErrorMessage(error as AxiosError<SchoolConnectSignupError>)
      );
    } finally {
      setIsSigningUp(false);
    }
  };

  return (
    <ConnectCard eyebrow="Realworld School" title="스쿨 계정 연결">
      <div css={styles.body} data-testid="connect-state-unauthenticated">
        <p css={styles.description}>
          연동 정보를 확인했어요.
          <br />
          Realworld 계정으로 로그인하면 연결을 완료할 수 있어요.
        </p>

        <UserInfoCard email={email} nickname={nickname} maskEmail />

        <div css={styles.loginArea}>
          <Countdown remainingSeconds={remainingSeconds} />
          <Button type="primary" size="medium" onClick={onLogin} cssStyle={styles.button}>
            로그인하고 계속하기
          </Button>
        </div>

        <form css={styles.signupForm} onSubmit={handleSignup}>
          <p css={styles.signupTitle}>새 Realworld 계정으로 계속하기</p>
          <FormGroup label="이메일">
            <Input value={email} disabled />
          </FormGroup>
          <FormGroup
            label="표시명"
            errorText={
              displayName && !NICKNAME_PATTERN.test(displayName)
                ? '2~20자 영문, 숫자, 하이픈, 언더스코어만 사용할 수 있어요'
                : undefined
            }
          >
            <Input
              value={displayName}
              placeholder="nickname"
              onChange={(value) => setDisplayName(value)}
            />
          </FormGroup>
          <FormGroup
            label="비밀번호"
            errorText={
              password && !PASSWORD_PATTERN.test(password)
                ? '8~20자, 대문자 1개 이상, 숫자 1개 이상을 포함해야 해요'
                : undefined
            }
          >
            <Input
              value={password}
              type="password"
              placeholder="Password1"
              onChange={(value) => setPassword(value)}
            />
          </FormGroup>
          <div css={styles.consentList}>
            <CheckBox
              checked={useConsent}
              onChange={(event) => setUseConsent(event.target.checked)}
            >
              [임시] 리얼월드 스튜디오 이용약관에 동의합니다.
            </CheckBox>
            <CheckBox
              checked={userDataConsent}
              onChange={(event) => setUserDataConsent(event.target.checked)}
            >
              [임시] 개인정보 수집 및 이용에 동의합니다.
            </CheckBox>
            <CheckBox
              checked={ageConsent}
              onChange={(event) => setAgeConsent(event.target.checked)}
            >
              [임시] 만 14세 이상임을 확인합니다.
            </CheckBox>
          </div>
          {signupErrorMessage && <p css={styles.errorText}>{signupErrorMessage}</p>}
          <Button
            type="primary"
            size="medium"
            htmlType="submit"
            loading={isSigningUp}
            disabled={isSigningUp}
            cssStyle={styles.button}
          >
            가입하고 계속하기
          </Button>
        </form>
      </div>
    </ConnectCard>
  );
}

async function signupWithSchoolAuthCode(
  payload: SchoolConnectSignupRequest
): Promise<SchoolConnectSignupResponse> {
  const { data } = await coreApiWithoutAuth.post<CommonResponseModel<SchoolConnectSignupResponse>>(
    SIGNUP_ENDPOINT,
    payload,
    { skipGlobalErrorToast: true }
  );
  return data.data;
}

function validateSignupForm({
  nickname,
  password,
  useConsent,
  userDataConsent,
  ageConsent
}: {
  nickname: string;
  password: string;
  useConsent: boolean;
  userDataConsent: boolean;
  ageConsent: boolean;
}) {
  if (!NICKNAME_PATTERN.test(nickname.trim())) {
    return '표시명은 2~20자 영문, 숫자, 하이픈, 언더스코어만 사용할 수 있어요.';
  }

  if (!PASSWORD_PATTERN.test(password)) {
    return '비밀번호는 8~20자이며 대문자 1개 이상, 숫자 1개 이상을 포함해야 해요.';
  }

  if (!useConsent || !userDataConsent || !ageConsent) {
    return '필수 동의 항목을 모두 확인해 주세요.';
  }

  return null;
}

function resolveSignupErrorMessage(error: AxiosError<SchoolConnectSignupError>) {
  const status = error.response?.status;
  const errorCode = error.response?.data?.error || error.response?.data?.errorTitle;

  if (status === 409 || errorCode === 'AlreadyExistAccount' || errorCode === 'Conflict') {
    return '이미 가입된 이메일입니다. 기존 계정으로 로그인한 뒤 다시 시도해 주세요.';
  }
  if (status === 410 || errorCode === 'AuthCodeExpiredOrUsed') {
    return '연동 코드가 만료되었거나 이미 사용되었습니다. 다시 발급해 주세요.';
  }
  if (status === 400) {
    return error.response?.data?.message || '입력값을 다시 확인해 주세요.';
  }

  return '가입 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
}

const styles = {
  body: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.medium};
  `,
  description: css`
    margin: 0;
    color: ${color.text_default};
    font-size: ${typography.size.s2};
    line-height: 1.5;
  `,
  loginArea: css`
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.small};
  `,
  signupForm: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.medium};
    padding-top: ${spacing.common.medium};
    border-top: 1px solid ${color.border_default_subdued};
  `,
  signupTitle: css`
    margin: 0;
    color: ${color.text_default};
    font-size: ${typography.size.s2};
    font-weight: ${typography.weight.medium};
    line-height: 1.5;
  `,
  consentList: css`
    display: flex;
    flex-direction: column;
    gap: ${spacing.common.small};
    color: ${color.text_default};
    font-size: ${typography.size.s2};
    line-height: 1.5;
  `,
  errorText: css`
    margin: 0;
    color: ${color.text_default};
    font-size: ${typography.size.s2};
    line-height: 1.5;
  `,
  button: css`
    width: 100%;
    font-weight: ${typography.weight.medium};
  `
};
